import React from 'react';
import _ from 'lodash';

import { PageInfo } from 'generated/graphql';
import testData from 'testData.json';



export interface IMockReturnAuthorization {
  id: string;
  intxNumber: string;
  store: {
    name: string;
  };

  createdTimestamp: string;

  customer: {
    firstName: string;
    lastName: string;
    email: string;
    hasEmailPermission: string;
    phoneNumber: string;
    smsPermission: string;
    contactMethod: string;
  };

  problems: string[];
  customerComments: string;
  trackingNumber: string;
  itemReceipt: {
    model: string;
    sku: string;
    createdTimestamp: string;
    serialNumber: string;
    softwareVersion: string;
    netSuiteRecord: {
      createdAt: string;
      urlToRecord: string;
    } | null;
  } | null;
  netSuiteRecord: {
    createdAt: string;
    urlToRecord: string;
  } | null;
  referenceNumber: string;
  rmaType: string;
  observation: {
    failureText: string;
    createdTimestamp: string;
    technician: {
      fullName: string;
    };
  } | null;
  repair: {
    repairCode: string;
    createdTimestamp: string;
    technician: {
      fullName: string;
    };
  } | null;
  replacement: {
    salesOrderNumber: string;
    shipDate: string;
    trackingNumber: string;
    serialNumber: string;
    createdTimestamp: string;
    technician: {
      fullName: string;
    };
  } | null;
}

export interface IMockQuery {
  called: boolean;
  loading: boolean;
  data?: {
    returnAuthorizations: {
      pageInfo: PageInfo;
      nodes: IMockReturnAuthorization[];
    };
  };
}



export function useMockTier1ReturnAuthorizations() {
  const [called, setCalled] = React.useState(false);
  const [state, setState] = React.useState<IMockReturnAuthorization[]>([]);

  React.useEffect(
    () => {
      setCalled(true);
      setTimeout(() => {
        const nodes = _.map(testData, data => __testDataRecord2MockReturnAuthorization(data));
        setState(nodes);
      }, 250);
    },
    []
  );

  return React.useMemo(
    (): IMockQuery => {
      return {
        called,
        loading: _.isEmpty(state),
        data: {
          returnAuthorizations: {
            pageInfo: {
              totalRows: state.length,
              pages: []
            },
            nodes: [...state]
          }
        }
      };
    },
    [called, state]
  );
}



export function useMockTier1ReturnAuthorizationsLazyQuery() {
  const [state, setState] = React.useState<IMockReturnAuthorization[]>([]);
  const [called, setCalled] = React.useState(false);
  const [loading, setLoading] = React.useState(false);



  const fnQuery = React.useCallback(
    ({
      variables
    }: {
      variables: {
        searchText: string;
      };
    }) => {
      setCalled(true);
      setLoading(true);
      return new Promise<IMockQuery>(resolve => {
        setTimeout(() => {
          const mappedRecords = _.map(testData, record => __testDataRecord2MockReturnAuthorization(record));
          const newState = _.filter(mappedRecords, record => {
            const fieldsToCheck = _.map([
              record.referenceNumber,
              record.itemReceipt?.serialNumber ?? '',
              record.intxNumber
            ], field => _.toLower(field));
            const loweredSearchText = _.toLower(variables.searchText);

            return _.some(fieldsToCheck, field => _.includes(field, loweredSearchText));
          });

          setState(newState);
          setLoading(false);
          resolve({
            called,
            loading: false,
            data: {
              returnAuthorizations: {
                pageInfo: {
                  totalRows: newState.length,
                  pages: []
                },
                nodes: newState
              }
            }
          });
        }, 250);
      });
    },
    [called]
  );



  const wrappedTuple = React.useMemo(
    (): IMockQuery => ({
      called,
      loading,
      data: {
        returnAuthorizations: {
          pageInfo: {
            totalRows: state.length,
            pages: []
          },
          nodes: [...state]
        }
      }
    }),
    [called, loading, state]
  );

  return React.useMemo(
    () => {
      return [
        fnQuery,
        wrappedTuple
      ] as const;
    },
    [fnQuery, wrappedTuple]
  );
}



export function useGetMockReturnAuthorization(id: string) {
  const [state, setState] = React.useState<IMockReturnAuthorization[]>([]);

  React.useEffect(
    () => {
      setTimeout(() => {
        setState(_.map(testData, record => __testDataRecord2MockReturnAuthorization(record)));
      }, 250);
    },
    []
  );

  return React.useMemo(
    () => {
      return {
        loading: _.isEmpty(state),
        data: {
          returnAuthorization: _.find(state, ra => ra.id === id) || null
        }
      };
    },
    [id, state]
  );
}



function __testDataRecord2MockReturnAuthorization(record: typeof testData[number]): IMockReturnAuthorization {
  return {
    id: record.id,
    createdTimestamp: record.createdTimestamp,
    problems: record.problems,
    netSuiteRecord: record.netSuiteRecord.id ? {
      createdAt: record.netSuiteRecord.createdTimestamp,
      urlToRecord: record.netSuiteRecord.internalId.toString()
    } : null,
    trackingNumber: record.trackingNumber,
    observation: record.observation.id ? {
      createdTimestamp: record.observation.createdTimestamp,
      failureText: record.observation.failureText,
      technician: {
        fullName: record.observation.technician.fullName
      }
    } : null,
    referenceNumber: record.referenceNumber,
    repair: record.repair.id ? {
      createdTimestamp: record.repair.createdTimestamp,
      repairCode: record.repair.repairCode,
      technician: {
        fullName: record.repair.technician.fullName
      }
    } : null,
    replacement: record.replacement.id ? {
      createdTimestamp: record.replacement.createdTimestamp,
      salesOrderNumber: record.replacement.salesOrderNumber,
      serialNumber: record.replacement.serialNumber,
      shipDate: record.replacement.shipTimestamp,
      trackingNumber: record.replacement.trackingNumber,
      technician: {
        fullName: record.replacement.technician.fullName
      }
    } : null,
    rmaType: record.rmaType,
    store: {
      name: record.store.name
    },
    customer: {
      contactMethod: record.customer.contactMethod,
      email: record.customer.email,
      firstName: record.customer.firstName,
      hasEmailPermission: record.customer.hasEmailPermission,
      lastName: record.customer.lastName,
      phoneNumber: record.customer.phoneNumber,
      smsPermission: record.customer.smsPermission
    },
    customerComments: record.customerComments,
    intxNumber: record.intxNumber,
    itemReceipt: record.itemReceipt.id ? {
      createdTimestamp: record.itemReceipt.createdTimestamp,
      model: record.itemReceipt.model,
      serialNumber: record.itemReceipt.serialNumber,
      sku: record.itemReceipt.sku,
      softwareVersion: record.itemReceipt.softwareVersion,
      netSuiteRecord: record.itemReceipt.netSuiteRecord.id ? {
        createdAt: record.itemReceipt.netSuiteRecord.createdTimestamp,
        urlToRecord: record.itemReceipt.netSuiteRecord.internalId.toString()
      } : null
    } : null
  };
}
